import { useAuth } from '@/features/auth/hooks/useAuth';
import type { TenantPermissions } from '@/lib/queries';

/**
 * Derive a human-readable role label from the permission set.
 *
 * Heuristic:
 * - "admin"  — has Identity Hub write permissions (user management)
 * - "editor" — has any POST/PUT/DELETE/PATCH action permissions
 * - "viewer" — read-only (only PAGE/GET permissions)
 */
export function deriveUserRole(permissions: TenantPermissions['permissions']): string {
  const hasIdentityWrite = permissions.some(
    (p) => p.type === 'ACTION' && ['POST', 'PUT', 'DELETE'].includes(p.method) && p.route.startsWith('/users'),
  );
  if (hasIdentityWrite) return 'admin';

  const hasAnyWrite = permissions.some(
    (p) => p.type === 'ACTION' && ['POST', 'PUT', 'DELETE', 'PATCH'].includes(p.method),
  );
  if (hasAnyWrite) return 'editor';

  return 'viewer';
}

/**
 * Get common event properties from the auth store.
 * Call at event-fire time to capture current state.
 */
export function getCommonEventProps(): {
  user_id: string;
  user_role: string;
  tenant_id: string;
} {
  const state = useAuth.getState();
  const activeTenant = state.tenants.find((t) => t.id === state.activeTenantId);
  const permissions = activeTenant?.permissions ?? [];

  return {
    user_id: state.user?.sub ?? '',
    user_role: deriveUserRole(permissions),
    tenant_id: state.activeTenantId ?? '',
  };
}
